// Collection sheets: the paper a treasurer writes cash dues on during one collection session
// (say, Sunday's service). ONE photo of the sheet is the proof for every payment written on it, so
// photos are kept per sheet, not per payment. Money is ALWAYS integer ngwee here.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'
import { fetchAllRows } from './paging'

export interface Sheet {
  id: string
  departmentId: string
  sheetOn: string
  label: string | null
  note: string | null
  /** Payments written on it that have not been cancelled, and their total. */
  paymentCount: number
  totalNgwee: number
  /** Photos that finished uploading. */
  photoCount: number
}

type SheetRow = { id: string; department_id: string; sheet_on: string; label: string | null; note: string | null }

const COLS = 'id, department_id, sheet_on, label, note'

/** A department's sheets, newest first (the last 100). */
export async function listSheets(departmentId: string): Promise<Sheet[]> {
  const rows = unwrap(
    (await supabase.from('collection_sheet').select(COLS).eq('department_id', departmentId).order('sheet_on', { ascending: false }).order('created_at', { ascending: false }).limit(100)) as never,
    'Could not load the collection sheets.',
  ) as SheetRow[]
  if (rows.length === 0) return []
  const ids = rows.map((r) => r.id)
  const [gifts, photos] = await Promise.all([
    // Only payments that still stand: a cancelled one (status 'reversed') is no longer on the sheet.
    fetchAllRows<{ id: string; sheet_id: string; amount_ngwee: number }>((from, to) =>
      supabase.from('contribution').select('id, sheet_id, amount_ngwee').eq('department_id', departmentId).in('sheet_id', ids).eq('status', 'posted').is('reverses_id', null).order('id').range(from, to),
    ),
    supabase.from('attachment').select('sheet_id').eq('department_id', departmentId).eq('status', 'ready').in('sheet_id', ids),
  ])
  const sums = new Map<string, { n: number; total: number }>()
  for (const g of unwrap(gifts as never, 'Could not load the payments on the sheets.') as { sheet_id: string; amount_ngwee: number }[]) {
    const s = sums.get(g.sheet_id) ?? { n: 0, total: 0 }
    s.total += Number(g.amount_ngwee)
    s.n += 1
    sums.set(g.sheet_id, s)
  }
  const counts = new Map<string, number>()
  for (const p of unwrap(photos as never, 'Could not load the sheet photos.') as { sheet_id: string }[]) counts.set(p.sheet_id, (counts.get(p.sheet_id) ?? 0) + 1)
  return rows.map((r) => ({
    id: r.id,
    departmentId: r.department_id,
    sheetOn: r.sheet_on,
    label: r.label,
    note: r.note,
    paymentCount: sums.get(r.id)?.n ?? 0,
    totalNgwee: sums.get(r.id)?.total ?? 0,
    photoCount: counts.get(r.id) ?? 0,
  }))
}

export interface SheetInput {
  sheetOn: string
  label?: string
  note?: string
}

/**
 * Start a sheet. `id` is made by the screen, once per form: a retry whose first answer was lost
 * finds the sheet it already made instead of making a second.
 */
export async function createSheet(departmentId: string, input: SheetInput, id: string): Promise<string> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.sheetOn)) throw new DataError({ code: 'P0001', message: 'Choose the date of the collection.' })
  const userId = await currentUserId()
  const res = await supabase.from('collection_sheet').insert({
    id,
    department_id: departmentId,
    sheet_on: input.sheetOn,
    label: input.label?.trim() ? input.label.trim() : null,
    note: input.note?.trim() ? input.note.trim() : null,
    created_by: userId,
  })
  if (res.error && res.error.code !== '23505') throw new DataError(res.error, 'Could not save the collection sheet.')
  return id
}
